import { countryLabel } from "@/lib/analytics-countries";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { RefreshCw, Save } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { CountryFlagIcon } from "@/components/CountryFlagIcon";
import { adImageSrc } from "@/lib/image-src";
import { CATEGORIES } from "@/lib/categories";
import {
  BarList,
  CohortGrid,
  Funnel,
  Heatmap,
  Help,
  Metric,
  Panel,
} from "@/components/admin/analytics/parts";
import {
  BUCKET_LABEL,
  DEFAULT_FILTERS,
  DOMAIN_SCOPES,
  HOST_LABEL,
  METRIC_HELP,
  RANGE_PRESETS,
  isTrackingOptedOut,
  langLabel,
  planLabel,
  setTrackingOptOut,
  type AnalyticsFilters,
  type HostKind,
} from "@/lib/analytics";
import {
  getAnalyticsPrefs,
  getAnalyticsReport,
  saveAnalyticsPrefs,
} from "@/lib/analytics-admin.functions";
import { BotsTab } from "@/components/admin/analytics/BotsTab";
import { DetailSheet, type DetailTarget } from "@/components/admin/analytics/DetailSheet";


export const Route = createFileRoute("/_authenticated/admin/statistik")({
  head: () => ({
    meta: [
      { title: "Statistik — Administration | Sellavo" },
      { name: "description", content: "Besöksstatistik, källor, geografi och kohorter för Sellavo." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StatsPage,
});

const COLORS = ["#2d8a9e", "#5cbdb9", "#1a4a6e", "#0c2340", "#8fd3c7", "#417ea3", "#9bb7c9"];

/** Enhetlig dropdown i projektets stil. */
function Picker({
  value,
  onChange,
  options,
  width = "w-[190px]",
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  width?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`${width}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const CATEGORY_LABEL = new Map(CATEGORIES.map((c) => [c.slug, c.label]));

/** Landlista med flagga och svenskt namn. */
function CountryList({
  items,
  onSelect,
  selected,
}: {
  items: { name: string; value: number }[];
  onSelect?: (name: string) => void;
  selected?: string | null;
}) {
  const max = Math.max(1, ...items.map((i) => i.value));
  if (!items.length) return <p className="text-sm text-muted-foreground">Ingen data i perioden än.</p>;
  return (
    <ul className="space-y-1.5">
      {items.map((i) => {
        const isSelected = selected === i.name;
        const body = (
          <>
            <div
              className={`absolute inset-y-0 left-0 ${isSelected ? "bg-primary/35" : "bg-primary/15"}`}
              style={{ width: `${(i.value / max) * 100}%` }}
            />
            <div className="relative flex items-center justify-between gap-3 px-2 py-1.5 text-sm">
              <span className="flex min-w-0 items-center gap-2">
                <CountryFlagIcon countryCode={i.name} size="xs" />
                <span className="truncate">{countryLabel(i.name)}</span>
              </span>
              <span className="shrink-0 font-medium tabular-nums">
                {i.value.toLocaleString("sv-SE")}
              </span>
            </div>
          </>
        );
        return (
          <li key={i.name} className="relative overflow-hidden rounded-md">
            {onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(i.name)}
                className={`relative block w-full overflow-hidden rounded-md text-left transition-colors hover:bg-accent/40 ${
                  isSelected ? "ring-1 ring-primary" : ""
                }`}
              >
                {body}
              </button>
            ) : (
              body
            )}
          </li>
        );
      })}
    </ul>
  );
}

function StatsPage() {
  const fetchReport = useServerFn(getAnalyticsReport);
  const fetchPrefs = useServerFn(getAnalyticsPrefs);
  const savePrefs = useServerFn(saveAnalyticsPrefs);

  const [filters, setFilters] = useState<AnalyticsFilters>(DEFAULT_FILTERS);
  const [saved, setSaved] = useState(false);
  const [selfExcluded, setSelfExcluded] = useState(false);
  const loadedPrefs = useRef(false);

  useEffect(() => {
    setSelfExcluded(isTrackingOptedOut());
  }, []);


  const { data: prefs } = useQuery({ queryKey: ["analytics-prefs"], queryFn: () => fetchPrefs({}) });

  useEffect(() => {
    if (loadedPrefs.current || !prefs) return;
    loadedPrefs.current = true;
    setFilters((f) => ({ ...f, ...prefs }));
  }, [prefs]);

  const set = <K extends keyof AnalyticsFilters>(key: K, value: AnalyticsFilters[K]) =>
    setFilters((f) => ({ ...f, [key]: value }));

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["analytics-report", filters],
    queryFn: () => fetchReport({ data: filters }),
    refetchInterval: filters.refresh ? filters.refresh * 1000 : false,
    placeholderData: (prev) => prev,
  });

  const [detail, setDetail] = useState<DetailTarget | null>(null);
  const openDetail = (key: DetailTarget["key"], label: string, value?: string) =>
    setDetail({ key, label, value });

  const t = data?.totals;
  const prev = data?.previous ?? null;

  const hostSlices = useMemo(
    () =>
      (data?.hosts ?? []).map((h) => ({
        name: HOST_LABEL[h.name as HostKind] ?? h.name,
        value: h.value,
      })),
    [data?.hosts],
  );

  const chips = [
    filters.country !== "all" && {
      label: `Land: ${countryLabel(filters.country)}`,
      clear: () => setFilters((f) => ({ ...f, country: "all", region: "all", city: "all" })),
    },
    filters.region !== "all" && {
      label: `Region: ${filters.region}`,
      clear: () => setFilters((f) => ({ ...f, region: "all", city: "all" })),
    },
    filters.city !== "all" && {
      label: `Stad: ${filters.city}`,
      clear: () => setFilters((f) => ({ ...f, city: "all" })),
    },
    filters.referrer !== "all" && {
      label: `Källa: ${filters.referrer}`,
      clear: () => setFilters((f) => ({ ...f, referrer: "all" })),
    },
  ].filter(Boolean) as { label: string; clear: () => void }[];


  return (
    <AdminShell title="Statistik">
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        Allt som händer på sajten, anonymt och utan IP-adresser: besök, vilka prylar som tittas på,
        varifrån trafiken kommer, vilken domän den kom in på och hur folk svarar på cookie-bannern.
      </p>

      {/* Domänval – styr allt nedanför */}
      <div className="mt-6 rounded-lg border border-border bg-card p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 text-sm font-medium">
            Mäter på <Help text={METRIC_HELP.domain} />
          </div>
          <div className="inline-flex rounded-md border border-input p-0.5">
            {DOMAIN_SCOPES.map((s) => (
              <button
                key={s.value}
                type="button"
                title={s.hint}
                onClick={() => set("domain", s.value)}
                className={`rounded px-3 py-1.5 text-sm transition-colors ${
                  filters.domain === s.value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={filters.excludeAdmins}
                onCheckedChange={(v) => set("excludeAdmins", v)}
              />
              Räkna inte med admin-konton <Help text={METRIC_HELP.excludeAdmins} />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={selfExcluded}
                onCheckedChange={(v) => {
                  setTrackingOptOut(v);
                  setSelfExcluded(v);
                }}
              />
              Spåra inte den här webbläsaren <Help text={METRIC_HELP.excludeSelf} />
            </label>
          </div>
        </div>
      </div>

      {/* Filterfält */}
      <div className="mt-3 rounded-lg border border-border bg-card p-3">
        <div className="flex flex-wrap items-center gap-2">

          <Picker
            value={filters.preset}
            onChange={(v) => set("preset", v as AnalyticsFilters["preset"])}
            options={RANGE_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
            width="w-[170px]"
          />

          {filters.preset === "custom" && (
            <>
              <Input
                type="date"
                className="h-10 w-auto"
                value={filters.from?.slice(0, 10) ?? ""}
                onChange={(e) => set("from", new Date(e.target.value).toISOString())}
              />
              <span className="text-sm text-muted-foreground">–</span>
              <Input
                type="date"
                className="h-10 w-auto"
                value={filters.to?.slice(0, 10) ?? ""}
                onChange={(e) => {
                  const d = new Date(e.target.value);
                  d.setHours(23, 59, 59, 999);
                  set("to", d.toISOString());
                }}
              />
            </>
          )}

          <Picker
            value={filters.bucket}
            onChange={(v) => set("bucket", v as AnalyticsFilters["bucket"])}
            options={[
              { value: "auto", label: "Upplösning: auto" },
              { value: "hour", label: BUCKET_LABEL.hour },
              { value: "day", label: BUCKET_LABEL.day },
              { value: "week", label: BUCKET_LABEL.week },
            ]}
            width="w-[180px]"
          />

          <Picker
            value={filters.hostKind}
            onChange={(v) => set("hostKind", v as AnalyticsFilters["hostKind"])}
            options={[
              { value: "all", label: "Alla domäner" },
              ...(Object.keys(HOST_LABEL) as HostKind[]).map((k) => ({
                value: k,
                label: HOST_LABEL[k],
              })),
            ]}
          />

          <Picker
            value={filters.device}
            onChange={(v) => set("device", v)}
            options={[
              { value: "all", label: "Alla enheter" },
              { value: "mobil", label: "Mobil" },
              { value: "tablet", label: "Surfplatta" },
              { value: "dator", label: "Dator" },
            ]}
            width="w-[160px]"
          />

          <Picker
            value={filters.appMode}
            onChange={(v) => set("appMode", v as AnalyticsFilters["appMode"])}
            options={[
              { value: "all", label: "App + webbläsare" },
              { value: "pwa", label: "Bara installerad app" },
              { value: "browser", label: "Bara webbläsare" },
            ]}
          />


          <Picker
            value={filters.country}
            onChange={(v) => set("country", v)}
            options={[
              { value: "all", label: "Alla länder" },
              ...(data?.countries ?? [])
                .filter((c) => c.name)
                .map((c) => ({ value: c.name, label: countryLabel(c.name) })),
            ]}
            width="w-[170px]"
          />

          <Picker
            value={filters.authed}
            onChange={(v) => set("authed", v as AnalyticsFilters["authed"])}
            options={[
              { value: "all", label: "Inloggade + utloggade" },
              { value: "yes", label: "Bara inloggade" },
              { value: "no", label: "Bara utloggade" },
            ]}
            width="w-[200px]"
          />

          <Picker
            value={String(filters.refresh)}
            onChange={(v) => set("refresh", Number(v) as AnalyticsFilters["refresh"])}
            options={[
              { value: "0", label: "Uppdatera: manuellt" },
              { value: "30", label: "Var 30:e sekund" },
              { value: "60", label: "Varje minut" },
            ]}
            width="w-[190px]"
          />

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0 cursor-pointer accent-[hsl(var(--primary))]"
              checked={filters.compare}
              onChange={(e) => set("compare", e.target.checked)}
            />
            Jämför med föregående period
          </label>

          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Uppdatera
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              await savePrefs({ data: filters });
              setSaved(true);
              setTimeout(() => setSaved(false), 2000);
            }}
          >
            <Save className="h-4 w-4" /> {saved ? "Sparat!" : "Spara vy"}
          </Button>
        </div>
        {data && (
          <p className="mt-2 text-xs text-muted-foreground">
            {new Date(data.meta.from).toLocaleString("sv-SE")} –{" "}
            {new Date(data.meta.to).toLocaleString("sv-SE")} · {BUCKET_LABEL[data.meta.bucket]} ·{" "}
            {data.meta.rows.toLocaleString("sv-SE")} händelser
            {data.meta.sampled ? " (maxgräns nådd, äldre data utelämnad)" : ""}
          </p>
        )}
      </div>

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Filtrerar på:</span>
          {chips.map((c) => (
            <button
              key={c.label}
              type="button"
              onClick={c.clear}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-1 text-xs font-medium transition-colors hover:bg-primary/20"
            >
              {c.label} <span aria-hidden>×</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() =>
              setFilters((f) => ({ ...f, country: "all", region: "all", city: "all", referrer: "all" }))
            }
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            Rensa alla
          </button>
        </div>
      )}

      {!data ? (
        <p className="mt-8 text-muted-foreground">Laddar statistik…</p>
      ) : (
        <Tabs value={filters.tab} onValueChange={(v) => set("tab", v)} className="mt-6">
          <TabsList className="flex flex-wrap">
            <TabsTrigger value="overview">Översikt</TabsTrigger>
            <TabsTrigger value="traffic">Trafik</TabsTrigger>
            <TabsTrigger value="content">Innehåll</TabsTrigger>
            <TabsTrigger value="behaviour">Beteende</TabsTrigger>
            <TabsTrigger value="sources">Källor</TabsTrigger>

            <TabsTrigger value="geo">Geografi</TabsTrigger>
            <TabsTrigger value="devices">Enheter</TabsTrigger>
            <TabsTrigger value="pwa">App (PWA)</TabsTrigger>
            <TabsTrigger value="cookies">Cookies</TabsTrigger>
            <TabsTrigger value="bots">Bottar &amp; AI</TabsTrigger>
            <TabsTrigger value="cohorts">Kohorter</TabsTrigger>
            <TabsTrigger value="funnels">Trattar</TabsTrigger>
            <TabsTrigger value="live">Live</TabsTrigger>
          </TabsList>


          {/* Översikt */}
          <TabsContent value="overview" className="mt-4 space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Metric label="Besökare" value={t!.visitors} help={METRIC_HELP.visitors} previous={prev?.visitors} onClick={() => openDetail("visitors", "Besökare")} />
              <Metric label="Sessioner" value={t!.sessions} help={METRIC_HELP.sessions} previous={prev?.sessions} onClick={() => openDetail("sessions", "Sessioner")} />
              <Metric label="Sidvisningar" value={t!.pageviews} help={METRIC_HELP.pageviews} previous={prev?.pageviews} onClick={() => openDetail("pageviews", "Sidvisningar")} />
              <Metric label="Annonsvisningar" value={t!.adViews} help={METRIC_HELP.adViews} previous={prev?.adViews} onClick={() => openDetail("adViews", "Annonsvisningar")} />
              <Metric label="Kontakter" value={t!.contacts} help={METRIC_HELP.contacts} previous={prev?.contacts} onClick={() => openDetail("contacts", "Kontakter")} />
              <Metric label="Publiceringar" value={t!.publishes} help={METRIC_HELP.publishes} previous={prev?.publishes} onClick={() => openDetail("publishes", "Publiceringar")} />
              <Metric label="Sökningar" value={t!.searches} help={METRIC_HELP.searches} previous={prev?.searches} onClick={() => openDetail("searches", "Sökningar")} />
              <Metric label="Delningar" value={t!.shares} help={METRIC_HELP.shares} previous={prev?.shares} onClick={() => openDetail("shares", "Delningar")} />
              <Metric label="Nya besökare" value={t!.newVisitors} help={METRIC_HELP.newVisitors} previous={prev?.newVisitors} onClick={() => openDetail("newVisitors", "Nya besökare")} />
              <Metric
                label="Återkommande"
                value={t!.returningVisitors}
                help={METRIC_HELP.returningVisitors}
                previous={prev?.returningVisitors}
                onClick={() => openDetail("returningVisitors", "Återkommande besökare")}
              />

              <Metric
                label="Sidor / session"
                value={t!.pagesPerSession}
                help={METRIC_HELP.pagesPerSession}
                previous={prev?.pagesPerSession}
              />
              <Metric
                label="Kontaktgrad"
                value={t!.contactRate}
                suffix=" %"
                help={METRIC_HELP.contactRate}
                previous={prev?.contactRate}
              />
              <Metric
                label="Unika inloggningar"
                value={t!.uniqueLogins}
                help={METRIC_HELP.uniqueLogins}
                previous={prev?.uniqueLogins}
              />
              <Metric
                label="Inloggningar totalt"
                value={t!.logins}
                help={METRIC_HELP.logins}
                previous={prev?.logins}
                onClick={() => openDetail("logins", "Inloggningar")}
              />
              <Metric
                label="Loggade in flera gånger"
                value={t!.repeatLogins}
                help={METRIC_HELP.repeatLogins}
                previous={prev?.repeatLogins}
              />
              <Metric
                label="Inloggade besökare"
                value={t!.loggedInVisitors}
                help={METRIC_HELP.loggedInVisitors}
                previous={prev?.loggedInVisitors}
                onClick={() => openDetail("loggedInVisitors", "Inloggade besökare")}
              />

            </div>


            <Panel title="Besökare över tid" help="Antal unika besökare och sessioner per tidsintervall.">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.series}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="t" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RTooltip />
                    <Legend />
                    <Area type="monotone" dataKey="visitors" name="Besökare" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.25} />
                    <Area type="monotone" dataKey="sessions" name="Sessioner" stroke={COLORS[1]} fill={COLORS[1]} fillOpacity={0.2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </TabsContent>

          {/* Trafik */}
          <TabsContent value="traffic" className="mt-4 space-y-4">
            <Panel title="Alla mätvärden över tid" help="Jämför sidvisningar, annonsvisningar, kontakter och publiceringar i samma graf.">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.series}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="t" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="pageviews" name="Sidvisningar" stroke={COLORS[0]} dot={false} />
                    <Line type="monotone" dataKey="adViews" name="Annonsvisningar" stroke={COLORS[1]} dot={false} />
                    <Line type="monotone" dataKey="contacts" name="Kontakter" stroke={COLORS[2]} dot={false} />
                    <Line type="monotone" dataKey="publishes" name="Publiceringar" stroke={COLORS[4]} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Panel>
            <Panel title="Ny mot återkommande" help="Nya besökare syns för första gången, återkommande har varit här förut.">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Nya", value: t!.newVisitors },
                        { name: "Återkommande", value: t!.returningVisitors },
                      ]}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={90}
                      label
                    >
                      <Cell fill={COLORS[0]} />
                      <Cell fill={COLORS[1]} />
                    </Pie>
                    <RTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </TabsContent>

          {/* Innehåll */}
          <TabsContent value="content" className="mt-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Mest besökta sidor" help="Räknat på sidvisningar under perioden. Klicka på en sida för att se besöken bakom.">
              <BarList items={data.pages} onSelect={(name) => openDetail("path", `Sida: ${name}`, name)} />
            </Panel>

            <Panel title="Mest besökta prylar" help="Annonser sorterade efter antal visningar. Kontaktgrad visar hur många visningar som ledde till ett meddelande.">
              {data.ads.length === 0 ? (
                <p className="text-sm text-muted-foreground">Inga annonsvisningar i perioden än.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[520px] text-sm">
                    <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="py-2 pr-3 font-medium">Annons</th>
                        <th className="py-2 pr-3 font-medium">Visningar</th>
                        <th className="py-2 pr-3 font-medium">Unika</th>
                        <th className="py-2 pr-3 font-medium">Kontakter</th>
                        <th className="py-2 font-medium">Delningar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.ads.map((a) => (
                        <tr key={a.slug} className="border-t border-border">
                          <td className="max-w-[220px] py-2 pr-3">
                            <HoverCard openDelay={120}>
                              <HoverCardTrigger asChild>
                                <a
                                  href={`/admin/annons/${a.slug}`}
                                  className="block truncate underline-offset-2 hover:underline"
                                >
                                  {a.title}
                                </a>
                              </HoverCardTrigger>
                              <HoverCardContent className="w-72 p-3">
                                {a.imageUrl ? (
                                  <img
                                    src={adImageSrc(a.imageUrl)}
                                    alt={a.title}
                                    className="mb-2 h-32 w-full rounded-md object-cover"
                                    loading="lazy"
                                  />
                                ) : null}
                                <p className="font-heading text-sm font-semibold">{a.title}</p>
                                <p className="mt-1 text-xs text-muted-foreground">
                                  {[CATEGORY_LABEL.get(a.category) ?? a.category, a.location, a.status]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </p>
                                <p className="mt-1 text-sm font-medium">
                                  {a.price.toLocaleString("sv-SE")} kr
                                </p>
                                <p className="mt-2 text-xs text-muted-foreground">
                                  {a.views} visningar · {a.unique} unika · {a.contacts} kontakter ·{" "}
                                  {a.shares} delningar
                                </p>
                              </HoverCardContent>
                            </HoverCard>
                          </td>
                          <td className="py-2 pr-3 tabular-nums">{a.views}</td>
                          <td className="py-2 pr-3 tabular-nums">{a.unique}</td>
                          <td className="py-2 pr-3 tabular-nums">{a.contacts}</td>
                          <td className="py-2 tabular-nums">{a.shares}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </TabsContent>

          {/* Beteende */}
          <TabsContent value="behaviour" className="mt-4 space-y-4">
            <Panel title="När besöken sker" help={METRIC_HELP.heatmap}>
              <Heatmap cells={data.heatmap} />
            </Panel>
            <div className="grid gap-4 lg:grid-cols-2">
              <Panel title="Landningssidor" help={METRIC_HELP.landing}>
                <BarList items={data.landing} />
              </Panel>
              <Panel title="Utgångssidor" help={METRIC_HELP.exits}>
                <BarList items={data.exits} />
              </Panel>
              <Panel title="Klick på knappar" help={METRIC_HELP.clicks}>
                <BarList
                  items={data.clicks}
                  onSelect={(name) => openDetail("click", `Klick: ${name}`, name)}
                />
              </Panel>

              <Panel title="Skrolldjup per sida" help={METRIC_HELP.scroll}>
                <BarList items={data.scroll} unit=" %" />
              </Panel>
            </div>
          </TabsContent>


          {/* Källor */}
          <TabsContent value="sources" className="mt-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Domän" help="Skiljer trafik på sellavo.com från Lovable-domänerna.">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={hostSlices} dataKey="value" nameKey="name" outerRadius={90} label>
                      {hostSlices.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <RTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Panel>
            <Panel title="Varifrån besökarna kommer" help="Domänen besökaren kom ifrån. (direct) betyder att länken öppnats direkt. Klicka på en rad för att se besöken bakom.">
              <BarList
                items={data.referrers}
                selected={filters.referrer === "all" ? null : filters.referrer}
                onSelect={(name) => openDetail("referrer", `Källa: ${name}`, name)}
              />
            </Panel>

          </TabsContent>

          {/* Geografi */}
          <TabsContent value="geo" className="mt-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              Klicka på ett land eller en region för att filtrera hela statistiken – då visas bara
              regionerna och städerna som hör dit. Klicka igen, eller på chippet högst upp, för att
              släppa filtret.
            </p>
            <div className="grid gap-4 lg:grid-cols-3">
              <Panel title="Länder" help="Land härleds från förfrågan – ingen IP-adress sparas.">
                <CountryList
                  items={data.countries}
                  selected={filters.country === "all" ? null : filters.country}
                  onSelect={(name) =>
                    setFilters((f) => ({
                      ...f,
                      country: f.country === name ? "all" : name,
                      region: "all",
                      city: "all",
                    }))
                  }
                />
              </Panel>
              <Panel title="Regioner" help="Region eller län som besökaren surfar från. Klicka för att se städerna i regionen.">
                <BarList
                  items={data.regions}
                  selected={filters.region === "all" ? null : filters.region}
                  onSelect={(name) =>
                    setFilters((f) => ({
                      ...f,
                      region: f.region === name ? "all" : name,
                      city: "all",
                    }))
                  }
                />
              </Panel>
              <Panel title="Städer" help="Ungefärlig stad, härledd av nätverket. Klicka för att se enbart den staden.">
                <BarList
                  items={data.cities}
                  selected={filters.city === "all" ? null : filters.city}
                  onSelect={(name) => setFilters((f) => ({ ...f, city: f.city === name ? "all" : name }))}
                />
              </Panel>
            </div>
          </TabsContent>


          {/* Enheter */}
          <TabsContent value="devices" className="mt-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Enhetstyp" help="Mobil, surfplatta eller dator.">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.devices}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RTooltip />
                    <Bar dataKey="value" name="Besökare" fill={COLORS[0]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
            <Panel title="Webbläsare" help="Vilken webbläsare besökarna använder.">
              <BarList items={data.browsers} />
            </Panel>
            <Panel title="Operativsystem" help="Vilket system enheten kör.">
              <BarList items={data.systems} />
            </Panel>
            <Panel title="Språk och kontotyp" help="Webbläsarspråk samt fördelning mellan utloggade och kontotyper.">
              <BarList
                items={[
                  ...data.languages.map((l) => ({ name: langLabel(l.name), value: l.value })),
                  ...data.plans.map((p) => ({ name: planLabel(p.name), value: p.value })),
                ]}
              />
            </Panel>
          </TabsContent>

          {/* App (PWA) */}
          <TabsContent value="pwa" className="mt-4 space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Metric
                label="App-besökare"
                value={data.pwa.visitors}
                help="Unika besökare som körde Sellavo som installerad app (hemskärm) i stället för i webbläsaren."
              />
              <Metric
                label="App-sessioner"
                value={data.pwa.sessions}
                help="Besökstillfällen som skedde i den installerade appen."
              />
              <Metric
                label="Andel av besökare"
                value={data.pwa.visitorShare}
                suffix=" %"
                help="Hur stor del av alla besökare som använder appen."
              />
              <Metric
                label="Sidvisningar i appen"
                value={data.pwa.pageviews}
                help="Sidvisningar gjorda i den installerade appen."
              />
            </div>

            <Panel title="App mot webbläsare över tid" help="Antal händelser per period, uppdelat på installerad app och vanlig webbläsare.">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.pwa.series}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="t" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RTooltip />
                    <Bar dataKey="app" name="App" stackId="a" fill={COLORS[0]} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="browser" name="Webbläsare" stackId="a" fill={COLORS[1]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <div className="grid gap-4 lg:grid-cols-3">
              <Panel title="Städer med flest app-användare" help="Ungefärlig stad för besökare som kör appen.">
                <BarList items={data.pwa.cities} />
              </Panel>
              <Panel title="Regioner" help="Region eller län för app-användarna.">
                <BarList items={data.pwa.regions} />
              </Panel>
              <Panel title="Länder" help="Land för app-användarna.">
                <CountryList items={data.pwa.countries} />
              </Panel>
              <Panel title="Operativsystem" help="Vilket system app-användarna kör.">
                <BarList items={data.pwa.systems} />
              </Panel>
              <Panel title="Enhetstyp" help="Mobil, surfplatta eller dator bland app-användarna.">
                <BarList items={data.pwa.devices} />
              </Panel>
              <Panel title="Mest besökta sidor i appen" help="Vilka sidor app-användarna öppnar.">
                <BarList items={data.pwa.pages} />
              </Panel>
            </div>
          </TabsContent>

          {/* Cookies */}
          <TabsContent value="cookies" className="mt-4 space-y-4">
            <div className="grid gap-3 sm:grid-cols-4">
              <Metric label="Godkänner alla" value={data.cookies.all} help="Klickade Godkänn alla i bannern. Klicka för att se vilka besök det gäller." onClick={() => openDetail("consent", "Godkände alla cookies", "all")} />
              <Metric label="Endast nödvändiga" value={data.cookies.necessary} help="Valde bort analyscookies. Klicka för att se besöken bakom." onClick={() => openDetail("consent", "Valde endast nödvändiga", "necessary")} />
              <Metric label="Anpassat val" value={data.cookies.custom} help="Sparade eget val under Anpassa. Klicka för att se besöken bakom." onClick={() => openDetail("consent", "Anpassat cookieval", "custom")} />

              <Metric label="Ja-andel" value={data.cookies.acceptRate} suffix=" %" help={METRIC_HELP.acceptRate} />
              <Metric label="Fick se rutan" value={data.cookies.shown} help="Antal gånger cookie-rutan visades." />
              <Metric label="Svarade" value={data.cookies.answered} help="Antal som faktiskt tryckte på något." />
              <Metric label="Svarsfrekvens" value={data.cookies.answerRate} suffix=" %" help={METRIC_HELP.answerRate} />
              <Metric label="Tillät analys" value={data.cookies.analyticsYes} help={METRIC_HELP.analyticsYes} />
              <Metric label="Öppnade Anpassa" value={data.cookies.settingsOpened} help="Hur många som gick in i inställningarna." />
              <Metric label="Öppnade policyn" value={data.cookies.policyOpened} help="Klick på länken till cookiepolicyn." />
              <Metric label="Ändrade sig" value={data.cookies.changed} help="Besökare som gick tillbaka och ändrade sitt val." />
            </div>

            <Panel title="Cookiesvar över tid" help="Hur svaren fördelar sig per tidsintervall.">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.cookies.series}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="t" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RTooltip />
                    <Legend />
                    <Bar dataKey="all" name="Alla" stackId="c" fill={COLORS[0]} />
                    <Bar dataKey="necessary" name="Nödvändiga" stackId="c" fill={COLORS[1]} />
                    <Bar dataKey="custom" name="Anpassat" stackId="c" fill={COLORS[2]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
            <div className="grid gap-4 lg:grid-cols-2">
              <Panel title="Svar per domän" help="Skiljer sellavo.com från Lovable-domänerna.">
                <BarList
                  items={data.cookies.byHost.map((h) => ({
                    name: HOST_LABEL[h.name as HostKind] ?? h.name,
                    value: h.value,
                  }))}
                />
              </Panel>
              <Panel title="Svar per land" help="Var i världen svaren kommer ifrån.">
                <CountryList items={data.cookies.byCountry} />
              </Panel>
            </div>
          </TabsContent>

          {/* Bottar och AI */}
          <TabsContent value="bots" className="mt-4">
            <BotsTab filters={filters} />
          </TabsContent>



          {/* Kohorter */}
          <TabsContent value="cohorts" className="mt-4">
            <Panel
              title="Återkomst per startvecka"
              help="Varje rad är besökare som kom första gången en viss vecka. Siffrorna visar hur stor andel som var tillbaka vecka 1, 2, 3 och så vidare."
            >
              <CohortGrid rows={data.cohorts} />
            </Panel>
          </TabsContent>

          {/* Trattar */}
          <TabsContent value="funnels" className="mt-4 grid gap-4 lg:grid-cols-2">
            {data.funnels.map((f) => (
              <Panel key={f.label} title={f.label} help="Andelen räknas mot första steget.">
                <Funnel steps={f.steps} />
              </Panel>
            ))}
          </TabsContent>

          {/* Live */}
          <TabsContent value="live" className="mt-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Tips: sätt uppdatering till 30 sekunder i filterfältet så tickar siffrorna på av sig
              själva. <Help text="Live räknar besökare som gjort något de senaste minuterna inom det valda tidsspannet." />
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Metric label="Senaste 5 min" value={data.live.last5} help="Unika besökare de senaste fem minuterna." />
              <Metric label="Senaste 30 min" value={data.live.last30} help="Unika besökare den senaste halvtimmen." />
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <Panel title="Sidor just nu" help="Sidor som visats den senaste halvtimmen.">
                <BarList items={data.live.pages} />
              </Panel>
              <Panel title="Länder just nu" help="Var besökarna är den senaste halvtimmen.">
                <CountryList items={data.live.countries} />
              </Panel>
            </div>
          </TabsContent>
        </Tabs>
      )}

      <DetailSheet target={detail} filters={filters} onClose={() => setDetail(null)} />
    </AdminShell>

  );
}
